/**
 * @fileoverview Admin service for Firestore operations.
 * 
 * This service provides secure admin operations for managing
 * reported content, user contributions, and other admin tasks.
 */

import { db } from '../firebase/config';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { 
  collection, 
  query, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc, 
  deleteDoc, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  addDoc
} from 'firebase/firestore';
import adminAuthServiceGmail from './adminAuthServiceGmail';
import inputValidationService from './inputValidationService';

interface ReportedContent {
  id: string;
  gameType: string;
  questionId: string;
  questionText: string;
  reportReason: string;
  improvementSuggestion?: string;
  timestamp: Date;
  status: 'pending' | 'reviewed' | 'resolved';
  reporterId: string;
  reporterEmail: string;
  // Enhanced fields for better admin management
  collectionId?: string;
  language?: string;
  originalQuestionData?: any;
  adminNotes?: string;
  reviewedAt?: Date;
  reviewedBy?: string;
  action?: string;
}

interface AdminAction {
  action: string;
  targetId: string;
  targetType: string;
  details: string;
  timestamp: Date;
  adminEmail: string;
}

class AdminService {
  private readonly BATCH_SIZE = 50;

  /**
   * Get all reported content
   */
  async getReportedContent(status?: 'pending' | 'reviewed' | 'resolved'): Promise<ReportedContent[]> {
    if (!adminAuthServiceGmail.isAdmin()) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      let q = query(
        collection(db, 'reported'),
        orderBy('timestamp', 'desc')
      );

      if (status) {
        q = query(q, where('status', '==', status));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      })) as ReportedContent[];
    } catch (error) {
      console.error('Error fetching reported content:', error);
      throw new Error('Failed to fetch reported content');
    }
  }

  /**
   * Update reported content status
   */
  async updateReportStatus(reportId: string, status: 'reviewed' | 'resolved', action?: string): Promise<void> {
    if (!adminAuthServiceGmail.isAdmin()) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      const reportRef = doc(db, 'reported', reportId);
      await updateDoc(reportRef, {
        status,
        reviewedAt: Timestamp.now(),
        reviewedBy: adminAuthServiceGmail.getCurrentUser()?.email,
        action: action || 'Status updated'
      });

      // Log admin action
      await this.logAdminAction('UPDATE_REPORT_STATUS', reportId, 'reported', 
        `Status changed to ${status}${action ? `: ${action}` : ''}`);

    } catch (error) {
      console.error('Error updating report status:', error);
      throw new Error('Failed to update report status');
    }
  }

  /**
   * Update report with additional fields
   */
  async updateReport(reportId: string, updates: any): Promise<void> {
    if (!adminAuthServiceGmail.isAdmin()) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      const reportRef = doc(db, 'reported', reportId);
      await updateDoc(reportRef, {
        ...updates,
        reviewedAt: updates.reviewedAt || Timestamp.now(),
        reviewedBy: updates.reviewedBy || adminAuthServiceGmail.getCurrentUser()?.email
      });

      // Log admin action
      await this.logAdminAction('UPDATE_REPORT', reportId, 'reported', 
        `Updated: ${Object.keys(updates).join(', ')}`);

    } catch (error) {
      console.error('Error updating report:', error);
      throw new Error('Failed to update report');
    }
  }

  /**
   * Get original question data from the source collection
   */
  async getOriginalQuestionData(questionId: string, collectionId: string): Promise<any> {
    if (!adminAuthServiceGmail.isAdmin()) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      // First try to get by document ID (in case the questionId is the actual document ID)
      try {
        const questionRef = doc(db, collectionId, questionId);
        const questionDoc = await getDoc(questionRef);
        
        if (questionDoc.exists()) {
          return {
            id: questionDoc.id,
            ...questionDoc.data()
          };
        }
      } catch (docError) {
        console.log('Document ID lookup failed, trying field query...');
      }

      // If document ID lookup fails, try to query by the 'id' field
      const questionsRef = collection(db, collectionId);
      const q = query(questionsRef, where('id', '==', parseInt(questionId) || questionId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const questionDoc = querySnapshot.docs[0];
        return {
          id: questionDoc.id,
          ...questionDoc.data()
        };
      }

      throw new Error('Question not found');
    } catch (error) {
      console.error('Error fetching original question data:', error);
      throw new Error(`Failed to fetch original question data Collection:${collectionId}`);
    }
  }

  /**
   * Update question content (e.g., mark as reported, disable)
   */
  async updateQuestionContent(questionId: string, gameType: string, updates: any, language: string = 'en', questionType?: string): Promise<void> {
    if (!adminAuthServiceGmail.isAdmin()) {
      throw new Error('Unauthorized: Admin access required');
    }

    // Validate updates
    const validation = inputValidationService.validateText(updates.reason || '', 'reportReason');
    if (!validation.isValid) {
      throw new Error(`Invalid input: ${validation.errors.join(', ')}`);
    }

    try {
      const collectionName = this.getCollectionName(gameType, language, questionType);
      
      // First try to get by document ID (in case the questionId is the actual document ID)
      let questionRef;
      try {
        questionRef = doc(db, collectionName, questionId);
        const questionDoc = await getDoc(questionRef);
        
        if (!questionDoc.exists()) {
          throw new Error('Document not found by ID');
        }
      } catch (docError) {
        // If document ID lookup fails, try to query by the 'id' field
        const questionsRef = collection(db, collectionName);
        const q = query(questionsRef, where('id', '==', parseInt(questionId) || questionId));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          throw new Error('Question not found');
        }
        
        questionRef = doc(db, collectionName, querySnapshot.docs[0].id);
      }
      
      await updateDoc(questionRef, {
        ...updates,
        updatedAt: Timestamp.now(),
        updatedBy: adminAuthServiceGmail.getCurrentUser()?.email
      });

      // Log admin action
      await this.logAdminAction('UPDATE_QUESTION', questionId, collectionName, 
        `Updated: ${Object.keys(updates).join(', ')}`);

    } catch (error) {
      console.error('Error updating question content:', error);
      throw new Error('Failed to update question content');
    }
  }

  /**
   * Get user contributions for review
   */
  async getUserContributions(status?: 'pending' | 'approved' | 'rejected'): Promise<any[]> {
    if (!adminAuthServiceGmail.isAdmin()) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      let q = query(
        collection(db, 'userContributions'),
        orderBy('timestamp', 'desc')
      );

      if (status) {
        q = query(q, where('status', '==', status));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));
    } catch (error) {
      console.error('Error fetching user contributions:', error);
      throw new Error('Failed to fetch user contributions');
    }
  }

  /**
   * Approve or reject user contribution
   */
  async reviewContribution(contributionId: string, status: 'approved' | 'rejected', reason?: string): Promise<void> {
    if (!adminAuthServiceGmail.isAdmin()) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      const contributionRef = doc(db, 'userContributions', contributionId);
      await updateDoc(contributionRef, {
        status,
        reviewedAt: Timestamp.now(),
        reviewedBy: adminAuthServiceGmail.getCurrentUser()?.email,
        reviewReason: reason || ''
      });

      // Log admin action
      await this.logAdminAction('REVIEW_CONTRIBUTION', contributionId, 'userContributions', 
        `Status: ${status}${reason ? `, Reason: ${reason}` : ''}`);

    } catch (error) {
      console.error('Error reviewing contribution:', error);
      throw new Error('Failed to review contribution');
    }
  }

  /**
   * Get admin dashboard statistics
   */
  async getDashboardStats(): Promise<any> {
    if (!adminAuthServiceGmail.isAdmin()) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      // Get reported content with error handling
      let reportedPending = [];
      let reportedTotal = [];
      let contributionsPending = [];

      try {
        reportedPending = await this.getReportedContent('pending');
      } catch (error: any) {
        console.warn('Could not fetch pending reports:', error.message);
      }

      try {
        reportedTotal = await this.getReportedContent();
      } catch (error: any) {
        console.warn('Could not fetch total reports:', error.message);
      }

      try {
        contributionsPending = await this.getUserContributions('pending');
      } catch (error: any) {
        console.warn('Could not fetch pending contributions:', error.message);
      }

      return {
        reportedPending: reportedPending.length,
        reportedTotal: reportedTotal.length,
        contributionsPending: contributionsPending.length,
        lastUpdated: new Date()
      };
    } catch (error: any) {
      console.error('Error fetching dashboard stats:', error);
      // Return default values instead of throwing
      return {
        reportedPending: 0,
        reportedTotal: 0,
        contributionsPending: 0,
        lastUpdated: new Date(),
        error: error.message
      };
    }
  }

  /**
   * Log admin actions for audit trail
   */
  private async logAdminAction(action: string, targetId: string, targetType: string, details: string): Promise<void> {
    try {
      const adminUser = adminAuthServiceGmail.getCurrentUser();
      if (!adminUser) return;

      const logEntry: AdminAction = {
        action,
        targetId,
        targetType,
        details,
        timestamp: new Date(),
        adminEmail: adminUser.email
      };

      await addDoc(collection(db, 'adminAuditLog'), logEntry);
    } catch (error) {
      console.error('Error logging admin action:', error);
      // Don't throw - logging failure shouldn't break admin operations
    }
  }

  /**
   * Get collection name based on game type and language
   * Note: Game questions are now served from CDN, not Firestore
   */
  private getCollectionName(gameType: string, language: string = 'en', questionType?: string): string {
    // Game questions are now served from CDN
    // This method is kept for backward compatibility but returns CDN info
    console.warn(`Game questions for ${gameType} (${language}) are now served from CDN, not Firestore`);
    
    // Return a placeholder - actual questions come from CDN
    return 'cdn_questions';
  }

  /**
   * Delete a user contribution
   */
  async deleteContribution(contributionId: string): Promise<{ success: boolean; error?: string }> {
    if (!adminAuthServiceGmail.isAdmin()) {
      return { success: false, error: 'Unauthorized: Admin access required' };
    }

    try {
      const contributionRef = doc(db, 'userContributions', contributionId);
      await deleteDoc(contributionRef);
      
      // Log the deletion
      await this.logAdminAction('DELETE_CONTRIBUTION', contributionId, 'userContributions', 
        'User contribution deleted');
      
      console.log('✅ User contribution deleted successfully:', contributionId);
      return { success: true };
    } catch (error) {
      console.error('Error deleting user contribution:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to delete user contribution' };
    }
  }

  /**
   * Bulk update operations (for efficiency)
   */
  async bulkUpdateReports(reportIds: string[], status: 'reviewed' | 'resolved'): Promise<void> {
    if (!adminAuthServiceGmail.isAdmin()) {
      throw new Error('Unauthorized: Admin access required');
    }

    try {
      const updates = reportIds.map(async (reportId) => {
        const reportRef = doc(db, 'reported', reportId);
        return updateDoc(reportRef, {
          status,
          reviewedAt: Timestamp.now(),
          reviewedBy: adminAuthServiceGmail.getCurrentUser()?.email,
          action: 'Bulk update'
        });
      });

      await Promise.all(updates);

      // Log bulk action
      await this.logAdminAction('BULK_UPDATE_REPORTS', reportIds.join(','), 'reported', 
        `Updated ${reportIds.length} reports to ${status}`);

    } catch (error) {
      console.error('Error bulk updating reports:', error);
      throw new Error('Failed to bulk update reports');
    }
  }
}

export const adminService = new AdminService();
export default adminService; 